<template>
  <v-app>
    <v-container v-if="!systemStatus?.donationGuideModule?.maintenanceMode" class="app-container">
      <v-dialog v-model="sessionError" max-width="420" persistent>
        <v-card elevation="0" flat>
          <v-alert border="start" class="ma-0 pb-0" colored-border type="error">
            <v-card-title>{{ t('system.sessionEnded.title') }}</v-card-title>
            <v-card-text>
              {{ t('system.sessionEnded.description') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" variant="text" @click="reloadLocation">
                {{ t('system.sessionEnded.reload') }}
              </v-btn>
            </v-card-actions>
          </v-alert>
        </v-card>
      </v-dialog>
      <!-- PREVIOUS TOP WARNING -->
      <!-- <div v-if="!!systemBanners && systemBanners.length > 0">
        <v-alert
          v-for="systemBanner in systemBanners"
          :key="systemBanner.id"
          border="start"
          :color="systemBanner.color"
          colored-border
          elevation="2"
          type="warning"
        >
          {{ systemBanner.text }}
        </v-alert>
      </div> -->
      <v-slide-x-transition mode="out-in">
        <div key="overlay-content">
          <donation-flow-wrapper
            :defined-step="definedStep"
            :multiple-donation-types="multipleDonationTypes"
            :request-has-external-agent="requestHasExternalAgent"
            :request-has-only-one-case="requestHasOnlyOneCase"
            :request-has-organization-group="requestHasOrganizationGroup"
            :stepper-ready="stepperReady"
          />
        </div>
      </v-slide-x-transition>
      <v-snackbar
        v-if="snackbar"
        v-model="snackbar.active"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
      >
        {{ snackbar.text }}
        <v-btn :class="{ 'float-right': !snackbar.action }" variant="text" @click="closeSnackbar">
          {{ t('generic.labels.close') }}
        </v-btn>
        <v-btn v-if="snackbar.action" :href="snackbar.action.url" variant="text" target="_blank">
          {{ snackbar.action.text }}
        </v-btn>
      </v-snackbar>

      <v-dialog
        v-model="dialog.active"
        max-width="450"
        persistent
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-text"
      >
        <v-card>
          <v-card-title id="dialog-title" class="headline secondary--text">
            {{ dialog.title }}
          </v-card-title>
          <v-card-text id="dialog-text">
            <p>{{ dialog.text }}</p>
            <p v-if="dialog.additionalText">
              {{ dialog.additionalText }}
            </p>
            <p v-if="dialog.warning">
              <b>{{ dialog.warning }}</b>
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              class="left"
              variant="text"
              :aria-label="dialog.closeBtnText"
              @click="closeDialog"
            >
              {{ dialog.closeBtnText }}
            </v-btn>
            <v-btn
              color="primary"
              variant="text"
              :aria-label="dialog.actionBtnText"
              @click="dialogActionClick"
            >
              {{ dialog.actionBtnText }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
    <v-container v-else class="app-container">
      <v-dialog :value="true" max-width="820" persistent>
        <v-card elevation="0" flat>
          <v-alert
            :icon="false"
            border="start"
            class="ma-0 pb-0 pt-0 pl-6"
            colored-border
            type="error"
          >
            <v-card-title>
              <v-icon class="mr-2" color="error"> mdi-alert </v-icon>
              {{ t('system.maintenance.operatingInformation') }}
            </v-card-title>
            <v-card-text>
              <p style="white-space: pre-line">
                {{ systemStatus?.donationGuideModule?.message || '' }}
              </p>
            </v-card-text>
            <v-card-actions>
              <v-img class="mb-6" contain height="35px" src="@/assets/min_gava_logo.png" />
            </v-card-actions>
          </v-alert>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, nextTick } from 'vue';
import { useTheme } from 'vuetify';
import { RequestSession } from '@/interfaces/requestSession';
import { ENDPOINTS, get, getErrorPageUrl, guideErrorMessage } from '@/api/mainApi';
import { Donation } from '@/interfaces/donation/donation';
import { Snackbar } from '@/interfaces/presentation/snackbar';
import { Dialog } from '@/interfaces/presentation/dialog';
import { SystemStatus } from '@/interfaces/system/systemStatus';
import { SystemBanner } from '@/interfaces/system/systemBanner';
import { BasaltSystemScope } from '@/enums/basaltSystemScope';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import DonationFlowWrapper from '@/components/flow/DonationFlowWrapper.vue';
import I18n from '@/plugins/i18n';
import { useLabel } from '@/composables/useLabel';
import store from '@/store';
import LocationService from '@/services/locationService';
import { isInIframe } from '@/utils';

export default defineComponent({
  name: 'MainApp',
  components: {
    DonationFlowWrapper,
  },
  setup() {
    const theme = useTheme();
    const { t } = useLabel();

    const getDonationStepFlowVersion = computed(
      () => store.state.requestSession?.donationGuideVersion ?? DonationGuideVersion.V1
    );

    const multipleDonationTypes = ref(false);
    const requestHasExternalAgent = ref(false);
    const requestHasOnlyOneCase = ref(false);
    const requestHasOrganizationGroup = ref(false);
    const sessionError = ref(false);
    const systemStatus = ref({
      donationGuideModule: {
        maintenanceMode: false,
        message: '',
      },
      adminModule: {
        maintenanceMode: false,
        message: '',
      },
    } as SystemStatus);
    const systemBanners = ref([] as SystemBanner[]);

    const steps = computed(() =>
      getDonationStepFlowVersion.value === DonationGuideVersion.V1 ? stepV1 : stepV2
    );

    const definedStep = ref<number | null>(null);
    const stepperReady = ref(false);

    const requestSession = computed((): RequestSession | null => store.state.requestSession);

    const donation = computed((): Donation | null => store.state.donation);

    const snackbar = computed((): Snackbar | null => store.state.snackbar);

    const dialog = computed((): Dialog => store.state.dialog!);

    const closeSnackbar = () => {
      store.commit('setSnackbar', { active: false });
    };

    const closeDialog = () => {
      store.commit('setDialog', { active: false });
    };

    const dialogActionClick = () => {
      dialog.value.actionClick();
      closeDialog();
    };

    const getSerialnumberParam = (): string | null => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      if (urlParams.has('serialnumber')) {
        return urlParams.get('serialnumber');
      }
      return null;
    };

    const getPaymentCodeParam = (): string | null => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      if (urlParams.has('paymentCode')) {
        return urlParams.get('paymentCode');
      }
      return null;
    };

    // Returns true when the browser is on a gateway cancel/fail redirect path.
    // In these cases we must never show the success page — even if the backend
    // webhook has already marked the donation as paid before the browser redirect.
    const isPaymentCancelledOrFailed = (): boolean => {
      const path = window.location.pathname.toLowerCase();
      return path.includes('/cancel') || path.includes('/fail') || path.includes('/error');
    };

    const getRequestSession = async (handleError: boolean) => {
      return await get(ENDPOINTS.SESSIONS_COOKIE_REQUEST, {}, handleError);
    };

    const setSessionInStore = (requestSession: RequestSession) => {
      store.commit('updateRequestSession', requestSession);
    };

    const reloadLocation = () => {
      location.reload();
    };

    const handleCustomTheming = (requestSession: RequestSession) => {
      if (requestSession) {
        // Try to update Vuetify 3 theme programmatically first.
        try {
          if (requestSession.primaryColor) {
            // update the in-memory theme colors
            if (theme?.themes?.value?.light?.colors) {
              theme.themes.value.light.colors.primary = '#' + requestSession.primaryColor;
            }
            // also set a CSS var fallback so any global styles pick it up
            document.documentElement.style.setProperty(
              '--v-theme-primary',
              '#' + requestSession.primaryColor
            );
          }
          if (requestSession.secondaryColor) {
            if (theme?.themes?.value?.light?.colors) {
              theme.themes.value.light.colors.secondary = '#' + requestSession.secondaryColor;
            }
            document.documentElement.style.setProperty(
              '--v-theme-secondary',
              '#' + requestSession.secondaryColor
            );
          }
          if (requestSession.accentColor) {
            if (theme?.themes?.value?.light?.colors) {
              theme.themes.value.light.colors.accent = '#' + requestSession.accentColor;
            }
            document.documentElement.style.setProperty(
              '--v-theme-accent',
              '#' + requestSession.accentColor
            );
          }
        } catch {
          // Fallback: if useTheme isn't available for some reason, set CSS variables directly
          if (requestSession.primaryColor)
            document.documentElement.style.setProperty(
              '--v-theme-primary',
              '#' + requestSession.primaryColor
            );
          if (requestSession.secondaryColor)
            document.documentElement.style.setProperty(
              '--v-theme-secondary',
              '#' + requestSession.secondaryColor
            );
          if (requestSession.accentColor)
            document.documentElement.style.setProperty(
              '--v-theme-accent',
              '#' + requestSession.accentColor
            );
        }
        if (!!requestSession.hideTaxReductionInfo) {
          store.commit('hideTaxReductionInfo', requestSession.hideTaxReductionInfo);
        }
      }
    };

    const handleDonationFromSerialnumber = async () => {
      let stepSet = false;
      const serialnumber = getSerialnumberParam();

      // Load request session so donationGuideVersion, organizationGroup, etc. are available
      const requestSessionRes: RequestSession = await getRequestSession(false);
      if (requestSessionRes) {
        setSessionInStore(requestSessionRes);
        setDonationGuideVersionFromRequestSession(requestSessionRes);
        multipleDonationTypes.value = requestSessionRes?.donationTypes?.length > 1;
        requestHasOnlyOneCase.value = requestSessionRes?.donationCases?.length === 1;
        requestHasExternalAgent.value = requestSessionRes?.externalAgent != null;
        requestHasOrganizationGroup.value = requestSessionRes?.organizationGroup?.id != null;
      } else {
        // Paylink visitors have no session cookie. Set a minimal stub so that other mounted
        // step components (which are kept alive via v-show) don't crash reading null properties.
        // donationGuideVersion must be set to V2 so that MainApp's `steps` computed (which reads
        // requestSession?.donationGuideVersion) agrees with store.state.donationGuideVersion
        // (which defaults to V2). Without this, steps resolves to V1 (PAYMENT_LINK=10) but flow
        // components look up step 10 against the V2 step list (max 9) → "Guide step 10 not found".
        setSessionInStore({
          donationTypes: [],
          paymentMethods: [],
          deliveryMethods: [],
          donationCases: [],
          organizationGroup: null,
          intermediary: null,
          incomingChannel: '',
          donationGuideVersion: DonationGuideVersion.V2,
        } as unknown as RequestSession);
      }

      const previousDonation: Donation = await get(
        ENDPOINTS.DONATIONS + '/' + ENDPOINTS.SERIALNUMBER + '/' + serialnumber
      );
      if (previousDonation) {
        store.commit('updateDonation', previousDonation);
        if (previousDonation.paymentSpecification.paid && !isPaymentCancelledOrFailed()) {
          definedStep.value = steps.value.SUCCESS;
          stepSet = true;
        } else {
          const paymentCode = getPaymentCodeParam();
          if (paymentCode) {
            definedStep.value = steps.value.PAYMENT_LINK;
            stepSet = true;
          }
          if (!stepSet) {
            definedStep.value = steps.value.PAYMENT;
          }
        }
      }
    };

    const getSystemStatus = async (): Promise<SystemStatus> => {
      return await get(ENDPOINTS.SYSTEM_STATUS, {}, false);
    };

    const getSystemBanners = async (): Promise<Array<SystemBanner>> => {
      return await get(
        ENDPOINTS.SYSTEM_BANNERS,
        { scope: Number(BasaltSystemScope.DONATION_GUIDE) },
        false
      );
    };

    const getUserLocation = async () => {
      try {
        const locationService = new LocationService();
        const country = await locationService.getUserCountry();
        store.commit('setLocation', country);
      } catch {
        store.commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'Failed to fetch user country',
        });
      }
    };

    const setDonationGuideVersion = (version: DonationGuideVersion) => {
      store.commit('setDonationGuideVersion', version);
    };

    const setDonationGuideVersionFromRequestSession = (requestSession: RequestSession) => {
      if (requestSession.donationGuideVersion) {
        setDonationGuideVersion(requestSession.donationGuideVersion);
      }
    };

    onMounted(async () => {
      try {
        systemStatus.value = (await getSystemStatus()) || systemStatus.value;
        try {
          await I18n.loadTranslations(I18n.getUserLanguage());
        } catch {
          // keep bundled locale files if remote translations fail
        }

        // Fetch system banners with error handling
        try {
          const banners = await getSystemBanners();
          if (Array.isArray(banners)) {
            systemBanners.value = banners;
          }
        } catch {
          systemBanners.value = [];
        }

        if (systemStatus.value?.donationGuideModule?.maintenanceMode) {
          return;
        }

        let requestSessionRes: RequestSession = await getRequestSession(false);
        // console.log('requestSessionRes (initial):', requestSessionRes);

        if ((getPaymentCodeParam() || !requestSessionRes) && getSerialnumberParam() != null) {
          await handleDonationFromSerialnumber();
        } else if (!requestSessionRes && isInIframe()) {
          window.location.href = getErrorPageUrl();
          return;
        } else {
          if (!requestSessionRes) {
            requestSessionRes = await getRequestSession(true);
          }
          // console.log('requestSessionRes (after true):', requestSessionRes);
          setSessionInStore(requestSessionRes);
          setDonationGuideVersionFromRequestSession(requestSessionRes);
          multipleDonationTypes.value = requestSessionRes?.donationTypes?.length > 1;
          requestHasOnlyOneCase.value = requestSessionRes?.donationCases?.length === 1;
          requestHasExternalAgent.value = requestSessionRes?.externalAgent != null;
          requestHasOrganizationGroup.value = requestSessionRes?.organizationGroup?.id != null;

          const urlSerialnumber = getSerialnumberParam();
          const sessionSerialnumber =
            requestSessionRes?.paymentResponse?.donationSerialnumber ?? null;
          const donationSerialnumber = urlSerialnumber || sessionSerialnumber;

          if (donationSerialnumber) {
            const previousDonation: Donation = await get(
              ENDPOINTS.DONATIONS + '/' + ENDPOINTS.SERIALNUMBER + '/' + donationSerialnumber
            );
            if (previousDonation) {
              store.commit('updateDonation', previousDonation);
              nextTick(() => {
                if (previousDonation.paymentSpecification.paid && !isPaymentCancelledOrFailed()) {
                  // if payment is paid and we are NOT on a cancel/fail URL, show success
                  definedStep.value = steps.value.SUCCESS;
                } else {
                  // else show payment view so the user can retry
                  definedStep.value = steps.value.PAYMENT;
                }
              });
            } else {
              definedStep.value = steps.value.DONATION_TYPE;
            }
          } else {
            // console.log('Setting definedStep to DONATION_TYPE');
            definedStep.value = steps.value.DONATION_TYPE;
          }
        }
        // Mark stepper as ready after all initialization with a small delay
        // console.log('Setting stepperReady to true, definedStep:', definedStep.value);
        nextTick(() => {
          setTimeout(() => {
            stepperReady.value = true;
          }, 100);
        });
        handleCustomTheming(requestSessionRes);
        getUserLocation();
      } catch (error) {
        window.log?.error('[Guide] bootstrap.init — Guide failed to initialize', {
          message: guideErrorMessage(error),
          inIframe: isInIframe(),
          donationGuideVersion: store.state.donationGuideVersion,
          organizationGroupId: store.state.requestSession?.organizationGroup?.id,
        });
        store.commit('setSnackbar', { active: true, color: 'error', text: 'Initialization error' });
      }
    });

    return {
      definedStep,
      t,
      multipleDonationTypes,
      requestHasExternalAgent,
      requestHasOnlyOneCase,
      requestHasOrganizationGroup,
      stepperReady,
      sessionError,
      systemStatus,
      systemBanners,
      requestSession,
      donation,
      snackbar,
      dialog,
      closeSnackbar,
      closeDialog,
      dialogActionClick,
      reloadLocation,
    };
  },
});
</script>

<style>
.theme--light.v-application {
  background: white !important;
}

.app-container {
  max-width: 800px !important;
}

.animated {
  transition: all 0.3s ease-out;
}

.multilined-item {
  white-space: initial;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
}

.v-card .v-card-title {
  display: flex !important;
  /* justify-content: flex-start !important; */
}

.v-card-actions {
  justify-content: flex-end;
}
</style>
